import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { menuItems } from '../../config/menuConfig';
import {
  FiLogOut,
  FiChevronDown,
  FiChevronRight
} from 'react-icons/fi';

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { user, logout } = useAuth();
  const { isAdmin, canView, hasPermissionsConfigured } = usePermissions();
  const [expandedMenus, setExpandedMenus] = useState(['dashboard']);



  const toggleMenu = (menuId) => {
    if (expandedMenus.includes(menuId)) {
      setExpandedMenus(expandedMenus.filter(id => id !== menuId));
    } else {
      setExpandedMenus([...expandedMenus, menuId]);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
    }
  };

  return (
    <aside className={`${
      collapsed ? 'lg:w-20' : 'lg:w-64'
    } ${
      mobileOpen ? 'translate-x-0' : '-translate-x-full'
    } w-64 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 fixed lg:static h-full z-50 lg:translate-x-0`}>
      {/* Institute Info */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-xl font-bold flex-shrink-0">
            {user?.name?.charAt(0) || 'I'}
          </div>
          <div className={`flex-1 min-w-0 ${collapsed ? 'hidden lg:hidden' : 'block'}`}>
            <h3 className="text-sm font-semibold text-gray-900 truncate">{user?.name}</h3>
            <span className="inline-block px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded mt-1">
              {user?.subscription_plan || 'Free'}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          
          // Filter by institute type: check if menu item is available for current institute type
          const instituteType = user?.institute_type || 'online';
          if (item.requiredTypes && !item.requiredTypes.includes(instituteType)) {
            return null;
          }
          
          // Filter by permission: show if admin, or if no permissions configured, or if has view permission
          if (item.menuId && !isAdmin && hasPermissionsConfigured && !canView(item.menuId)) {
            return null;
          }
          
          // Filter submenu items by institute type and permission
          let filteredSubmenu = item.submenu;
          if (item.submenu) {
            // First filter by institute type
            filteredSubmenu = item.submenu.filter(sub => {
              if (sub.requiredTypes && !sub.requiredTypes.includes(instituteType)) {
                return false;
              }
              return true;
            });
            
            // Then filter by permission
            if (!isAdmin && hasPermissionsConfigured) {
              filteredSubmenu = filteredSubmenu.filter(sub => !sub.menuId || canView(sub.menuId));
            }
          }
          
          // Don't show parent if all submenus are filtered out
          if (item.submenu && filteredSubmenu && filteredSubmenu.length === 0) {
            return null;
          }
          
          return (
            <div key={item.id}>
              {item.path ? (
                // Single menu item
                <NavLink
                  to={item.path}
                  onClick={() => setMobileOpen && setMobileOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-primary-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-xl flex-shrink-0"><IconComponent /></span>
                  <span className={`text-sm font-medium ${collapsed ? 'hidden lg:hidden' : 'block'}`}>{item.title}</span>
                </NavLink>
              ) : (
                // Menu with submenu
                <>
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => toggleMenu(item.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl flex-shrink-0"><IconComponent /></span>
                      <span className={`text-sm font-medium ${collapsed ? 'hidden lg:hidden' : 'block'}`}>{item.title}</span>
                    </div>
                    <span className={`text-gray-400 ${collapsed ? 'hidden lg:hidden' : 'block'}`}>
                      {expandedMenus.includes(item.id) ? (
                        <FiChevronDown />
                      ) : (
                        <FiChevronRight />
                      )}
                    </span>
                  </button>
                  {expandedMenus.includes(item.id) && (
                    <div className={`ml-9 mt-1 space-y-1 ${collapsed ? 'hidden lg:hidden' : 'block'}`}>
                      {filteredSubmenu.map((subItem, index) => (
                        <NavLink
                          key={index}
                          to={subItem.path}
                          onClick={() => setMobileOpen && setMobileOpen(false)}
                          className={({ isActive }) =>
                            `block px-3 py-2 text-sm rounded-lg transition-colors ${
                              isActive 
                                ? 'bg-primary-50 text-primary-700 font-medium' 
                                : 'text-gray-600 hover:bg-gray-50'
                            }`
                          }
                        >
                          {subItem.title}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div className="p-3 border-t border-gray-200">
        <button 
          className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          onClick={handleLogout}
        >
          <span className="text-xl flex-shrink-0">
            <FiLogOut />
          </span>
          <span className={`text-sm font-medium ${collapsed ? 'hidden lg:hidden' : 'block'}`}>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;

